/** One published factor — Batch 5's detail page for the factor library (`/factors/$factorId`).
 *
 * Every figure's receipt cites a factor; this is where one of them is read in full: its value and
 * unit, who published it, which edition and year, the source and its file hash, the GWP set, how it
 * was derived (as far as the library records that) and when its set was locked. It reads the same
 * `GET /api/v1/factor-sets` the library does and **finds the row there**; there is no endpoint for
 * a single factor, and adding a second read of the same fact would give it a second home.
 *
 * ## Used by is exact, and read from the workspace's own calculations
 *
 * `GET /api/v1/factors/usage` names the factor by the five parts that identify it in the library
 * (set, category, fuel or carrier, region, unit) and the server counts the calculations whose
 * `factor_id` is the engine's string for exactly those parts. Nothing here infers a use from a fuel
 * name: a figure for the same fuel priced by another edition or set has a different id and does not
 * appear. The counts are integers over the whole history (clamped to an Auditor's engagement), the
 * rows are the first few by size, and each opens the record and its receipt (rule 3). A read that
 * fails says so; it is never rendered as *"nothing used this factor"*.
 *
 * ## What is not here, and why each is said in words
 *
 * - **No sum of emissions across the figures.** A total of a window is `GET /inventory`'s, and
 *   states whether the window is closed. Each listed figure carries its own amount.
 * - **No control that edits.** A set is locked when it is loaded (ADR-0012); a publisher's
 *   correction is a new edition. The only button is *Try again*, and only after a failed read.
 * - **No method is invented.** The library records a value, its gas components and a note, not a
 *   derivation, so *Method* states that rather than describing how DESNZ or EPA computed it.
 *
 * ## Three states
 *
 * - **failed** — the shared `ReadFailed`, with the failure's own sentence and a retry. Never *"not
 *   found"*: a dropped connection is not a statement about the library.
 * - **loading** — said, not blank.
 * - **not found** — the read succeeded and no row has this name. One sentence for every cause (a
 *   stale link, a typo, an edition that was replaced), with the way back to the library.
 *
 * Rule 5: a value is the row's own decimal string, trimmed of the zeros the column added by
 * `factorValue`; nothing here is converted to a number. The untrimmed string stays in
 * `data-factor-value`. Brand green never colours a number, and the lock is a word and an icon.
 */

import { Link, useParams } from "@tanstack/react-router";
import type { ReactNode } from "react";

import { useFactorLibrary } from "../api/factors";
import { useFactorUsage } from "../api/factorUsage";
import type { FactorLibraryFactor, FactorLibrarySet } from "../api/factors";
import { Loaded } from "../components/Loaded";
import { PageHeader } from "../components/PageHeader";
import { Panel } from "../components/Panel";
import { StatusPill } from "../components/StatusPill";
import { factorValue, shortDate, tonnes } from "../lib/format";
import { RequireSession } from "../shell/RequireSession";
import { findFactor } from "./factorRoute";
import { RecordPanel, RecordTabs } from "./RecordTabs";
import { ScreenFrame } from "./ScreenFrame";
import { keepSearch } from "./search";
import { Facts } from "./ScreenParts";
import type { FactSpec } from "./ScreenParts";
import parts from "./ScreenParts.module.css";
import { SystemDisclosure } from "./SystemDisclosure";
import { useScreen } from "./useScreen";
import { useUrlTab } from "./useUrlTab";
import own from "./FactorLibrary.module.css";
import styles from "./Screen.module.css";

const TABS = [
  { key: "overview", label: "Overview" },
  { key: "provenance", label: "Provenance" },
  { key: "used-by", label: "Used by" },
] as const;

/** A value the row does not carry, said as a decision rather than left blank (KEEP #10). */
function Absent({ children }: { children: ReactNode }) {
  return (
    <span className={own.none} data-absent>
      {children}
    </span>
  );
}

function Fact({ name, label, children }: { name: string; label: string; children: ReactNode }) {
  return (
    <div className={own.fact} data-fact={name}>
      <dt>{label}</dt>
      <dd>{children}</dd>
    </div>
  );
}

/** A link only for an address that is a web page; anything else is shown as text and not followed. */
function SourceLink({ url }: { url: string }) {
  return /^https?:\/\//i.test(url) ? (
    <a href={url} target="_blank" rel="noreferrer noopener" className={own.url}>
      {url}
    </a>
  ) : (
    <span className={own.mono}>{url}</span>
  );
}

function yearOf(set: FactorLibrarySet): string | null {
  const match = set.published_at ? /^(\d{4})/.exec(set.published_at) : null;
  return match?.[1] ?? null;
}

function factsOf(set: FactorLibrarySet, factor: FactorLibraryFactor): FactSpec[] {
  const year = yearOf(set);
  return [
    {
      key: "factor-value",
      label: "Factor value",
      value: factorValue(factor.value),
      unit: ` ${factor.unit_out} per ${factor.unit_in}`,
      note: "Source check on record",
    },
    { key: "factor-edition", label: "Edition", value: factor.edition, textual: true, note: factor.publisher },
    year
      ? { key: "factor-year", label: "Published", value: year, note: set.published_at ? shortDate(set.published_at) : undefined }
      : {
          key: "factor-year",
          label: "Published",
          value: "Not stated",
          textual: true,
          note: "The publication date is not recorded for this set.",
        },
    { key: "factor-gwp", label: "GWP set", value: set.gwp_set_label, textual: true, note: "Recorded for the whole set" },
  ];
}

function OverviewTab({ set, factor }: { set: FactorLibrarySet; factor: FactorLibraryFactor }) {
  const gases = factor.components ? Object.entries(factor.components) : null;
  return (
    <>
      <Panel title="Value" sub={`${factor.category} · ${factor.fuel_or_carrier}`} column="1 / 13">
        <dl className={own.facts} data-testid="factor-detail-value">
          <Fact name="value" label="Value">
            <span data-factor-value={factor.value}>
              {factorValue(factor.value)} {factor.unit_out}
            </span>
          </Fact>
          <Fact name="unit_in" label="Per">
            {factor.unit_in}
          </Fact>
          <Fact name="category" label="Category">
            {factor.category}
          </Fact>
          <Fact name="fuel_or_carrier" label="Fuel or carrier">
            {factor.fuel_or_carrier}
          </Fact>
          <Fact name="region" label="Region">
            {factor.region === "" ? <Absent>all</Absent> : factor.region}
          </Fact>
          <Fact name="components" label="Components">
            {gases === null ? (
              <Absent>none published</Absent>
            ) : (
              gases.map(([gas, amount], index) => (
                <span key={gas}>
                  {index > 0 ? " · " : ""}
                  {gas} {factorValue(amount)}
                </span>
              ))
            )}
          </Fact>
          <Fact name="external_id" label="Publisher’s id">
            {factor.external_id ? <span className={own.mono}>{factor.external_id}</span> : <Absent>none given</Absent>}
          </Fact>
          <Fact name="verification_row" label="Source check">
            {/* Where the check is logged is our own reference (rule 2), not something a reader can look up:
                it is the hover and a data attribute, and the words say that a check is on record (U8). */}
            <span title={factor.verification_row} data-verification-row={factor.verification_row}>
              On record
            </span>
          </Fact>
        </dl>
        {factor.notes ? (
          <p className={styles.note} data-testid="factor-detail-notes">
            {factor.notes}
          </p>
        ) : (
          <p className={styles.note} data-testid="factor-detail-notes-absent">
            The publisher’s note for this factor is not recorded.
          </p>
        )}
      </Panel>
      <Panel title="Method" sub="How this value was arrived at, as far as the library records it" column="1 / 13">
        <p className={styles.note} data-testid="factor-detail-method">
          The library records no derivation for a factor. This is the publisher’s own value, not a
          calculation EmitIQ made, and how {factor.publisher} derived it is in the source on the
          Provenance tab.{" "}
          {gases === null
            ? "No split into gases was published for it."
            : "The publisher splits it into the gases above."}{" "}
          Converting to CO₂e uses the set’s GWP set, {set.gwp_set_label}.
        </p>
      </Panel>
      <Panel title="Factor set" sub="The jurisdiction’s published set this factor belongs to" column="1 / 13">
        <dl className={own.facts} data-testid="factor-detail-set">
          <Fact name="set" label="Set">
            {set.label}
          </Fact>
          <Fact name="jurisdiction" label="Jurisdiction">
            {set.jurisdiction}
          </Fact>
          <Fact name="set_key" label="Set key">
            <span className={own.mono}>{set.key}</span>
          </Fact>
          <Fact name="factors_in_set" label="Factors in the set">
            {set.factors.length}
          </Fact>
        </dl>
        <Link className={styles.fixLink} to="/factors" search={keepSearch} data-testid="factor-detail-library-link">
          See the whole set in the factor library ›
        </Link>
      </Panel>
    </>
  );
}

function ProvenanceTab({ set, factor }: { set: FactorLibrarySet; factor: FactorLibraryFactor }) {
  // The row's own source when it has one (the US set is two publications), else the set's. The
  // receipt resolves it the same way, so the two screens cannot cite different files.
  const origin = factor.source_url
    ? {
        url: factor.source_url,
        publishedAt: factor.source_published_at,
        sha256: factor.source_sha256,
      }
    : { url: set.source_url, publishedAt: set.published_at, sha256: set.source_sha256 };
  return (
    <Panel title="Provenance" sub="Who published it, which edition, and when it was retrieved" column="1 / 13">
      <dl className={own.facts} data-testid="factor-detail-provenance">
        <Fact name="publisher" label="Publisher">
          {factor.publisher}
        </Fact>
        <Fact name="edition" label="Edition">
          {factor.edition}
        </Fact>
        <Fact name="published_at" label="Published">
          {origin.publishedAt ? shortDate(origin.publishedAt) : <Absent>not stated</Absent>}
        </Fact>
        <Fact name="retrieved_at" label="Retrieved">
          {shortDate(set.retrieved_at)}
        </Fact>
        <Fact name="source" label="Source">
          {origin.url ? <SourceLink url={origin.url} /> : <Absent>no source recorded</Absent>}
        </Fact>
        <Fact name="source_sha256" label="File hash (SHA-256)">
          {origin.sha256 ? <span className={own.mono}>{origin.sha256}</span> : <Absent>no file hash recorded</Absent>}
        </Fact>
        <Fact name="gwp_set_label" label="GWP set">
          {set.gwp_set_label}
        </Fact>
        <Fact name="licence" label="Licence">
          {set.licence ?? <Absent>none recorded</Absent>}
        </Fact>
        <Fact name="locked" label="Locked">
          {set.locked ? (
            <StatusPill tone="ok" testId="factor-detail-locked">
              {set.locked_at ? shortDate(set.locked_at) : "yes"}
            </StatusPill>
          ) : (
            <StatusPill tone="crit" testId="factor-detail-locked">
              NOT LOCKED
            </StatusPill>
          )}
        </Fact>
      </dl>
      {set.attribution_statement ? (
        <p className={own.attribution} data-testid="factor-detail-attribution">
          {set.attribution_statement}
        </p>
      ) : (
        <p className={own.attribution} data-testid="factor-detail-attribution-absent">
          No attribution statement is recorded for this set.
        </p>
      )}
    </Panel>
  );
}

function UsedByTab({ set, factor }: { set: FactorLibrarySet; factor: FactorLibraryFactor }) {
  const usage = useFactorUsage({
    set_key: set.key,
    category: factor.category,
    fuel_or_carrier: factor.fuel_or_carrier,
    region: factor.region,
    unit_in: factor.unit_in,
  });
  return (
    <Panel title="Used by" sub="Which of this workspace’s figures this factor priced" column="1 / 13">
      <Loaded query={usage} of="Which figures this factor priced">
        {(read) => (
          <div data-testid="factor-used-by" data-in-force={read.in_force_calculations}>
            <dl className={own.facts} data-testid="factor-used-by-counts">
              <Fact name="in_force" label="Figures in force">
                {read.in_force_calculations}
              </Fact>
              <Fact name="records" label="Activity records">
                {read.activity_records}
              </Fact>
              <Fact name="replaced" label="Replaced since">
                {read.replaced_calculations}
              </Fact>
            </dl>
            {read.items.length === 0 ? (
              <p className={styles.note} data-testid="factor-used-by-none">
                {read.replaced_calculations > 0
                  ? "No figure in force was priced with this factor; the ones that were have since been replaced."
                  : "No figure in this workspace has been priced with this factor."}
              </p>
            ) : (
              <ul className={own.usedBy} data-testid="factor-used-by-list">
                {read.items.map((row) => (
                  <li key={row.calculation_id} data-calculation-id={row.calculation_id}>
                    <Link
                      className={styles.fixLink}
                      to="/records/$recordId"
                      params={{ recordId: row.activity_record_id }}
                      search={(prev) => ({ ...keepSearch(prev), receipt: row.calculation_id })}
                      data-testid="factor-used-by-record"
                    >
                      {row.facility_name} · {shortDate(row.period_start)} to {shortDate(row.period_end)}
                    </Link>
                    <span className={own.usedByMeta}>
                      Scope {row.scope}
                      {row.method ? ` · ${row.method}-based` : ""} · {tonnes(row.kgco2e, 3)} tCO₂e
                    </span>
                  </li>
                ))}
              </ul>
            )}
            <p className={styles.note} data-testid="factor-used-by-basis">
              {read.truncated
                ? `The ${read.items.length} largest of ${read.in_force_calculations} are listed; the counts are of all of them. `
                : ""}
              A figure is in force when it is the current calculation and its record has not since
              been corrected. A record priced under both Scope 2 methods is two figures and one
              record. Each row opens the record with the figure’s receipt beside it.
            </p>
          </div>
        )}
      </Loaded>
      <Link className={styles.fixLink} to="/emissions" search={keepSearch} data-testid="factor-used-by-emissions">
        Open the Emissions register ›
      </Link>
    </Panel>
  );
}

function FactorDetailBody() {
  const screen = useScreen();
  const { factorId } = useParams({ from: "/factors/$factorId" });
  const library = useFactorLibrary();
  const [tab, selectTab] = useUrlTab(TABS);
  const found = library.data ? findFactor(library.data.sets, factorId) : undefined;
  const label = TABS.find((entry) => entry.key === tab)?.label ?? tab;

  return (
    <ScreenFrame screen={screen} title="Factor" crumb="one published emission factor" bodyScrolls>
      <PageHeader
        title={found ? `${found.factor.fuel_or_carrier} · ${found.factor.category}` : "Factor"}
        subtitle={
          found
            ? `${found.set.label} · ${found.factor.publisher}, ${found.factor.edition}`
            : "One published factor: its value, who published it, and where it came from."
        }
        tabs={
          found ? (
            <RecordTabs
              tabs={TABS}
              tab={tab}
              onSelect={selectTab}
              label="Factor"
              saying={`Showing ${label} for ${found.factor.fuel_or_carrier}, ${found.factor.category}.`}
            />
          ) : undefined
        }
        actions={
          <>
            {found ? (
              found.set.locked ? (
                <StatusPill tone="ok" testId="factor-detail-header-lock">
                  Locked
                </StatusPill>
              ) : (
                <StatusPill tone="crit" testId="factor-detail-header-lock">
                  NOT LOCKED
                </StatusPill>
              )
            ) : null}
            <Link className={styles.fixLink} to="/factors" search={keepSearch}>
              Back to the factor library ›
            </Link>
          </>
        }
      />
      {found ? <Facts facts={factsOf(found.set, found.factor)} label="This factor at a glance" /> : null}
      <div className={parts.area}>
        {found ? (
          <RecordPanel tab={tab}>
            <div className={parts.stack}>
              {tab === "overview" ? <OverviewTab set={found.set} factor={found.factor} /> : null}
              {tab === "provenance" ? <ProvenanceTab set={found.set} factor={found.factor} /> : null}
              {tab === "used-by" ? <UsedByTab set={found.set} factor={found.factor} /> : null}
            </div>
          </RecordPanel>
        ) : (
          <Panel title="Factor" column="1 / 13">
            <Loaded query={library} of="The factor library">
              {() => (
                // The same sentence for every cause: a stale link, a typo, an edition that was
                // replaced. Telling them apart would need a second read of a fact with one home.
                <div data-testid="factor-detail-not-found">
                  <p className={styles.note}>
                    No factor in the library matches this link. A publisher’s correction is a new
                    edition, so a link to a factor from an earlier one can stop matching.
                  </p>
                  <Link className={styles.fixLink} to="/factors" search={keepSearch}>
                    Find it in the factor library ›
                  </Link>
                </div>
              )}
            </Loaded>
          </Panel>
        )}
      </div>
      <SystemDisclosure summary="Basis and limits: what this page is" testId="factor-detail-basis">
        <p className={styles.note}>
          Every figure’s receipt cites a factor like this one. A set is locked when it is loaded and a
          publisher’s correction becomes a new edition rather than a change to a factor, so this page
          only reads. Spend-based multiplier sets, used for purchases, are held separately and are not
          listed here.
        </p>
      </SystemDisclosure>
    </ScreenFrame>
  );
}

export function FactorDetailScreen() {
  return (
    <RequireSession>
      <FactorDetailBody />
    </RequireSession>
  );
}
